import Flickity from "./flickity-loader";
import {
  markFlickityDragEnd,
  markFlickityDragStart,
} from "./flickity-drag-state";

const DRAG_GUARD_KEY = "__flickityDragGuard";

const destroyFlickity = (el) => {
  el[DRAG_GUARD_KEY]?.abort();
  el[DRAG_GUARD_KEY] = null;

  const instance = Flickity.data(el);
  if (instance) {
    instance.destroy();
  }
};

const parseFlickityOptions = (el) => {
  const attr = el.getAttribute("data-flickity");
  if (!attr) {
    return {};
  }
  try {
    return JSON.parse(attr);
  } catch {
    return {};
  }
};

/** Prevent accidental navigation when the user drags the slider (featured blog, etc.). */
const attachDragClickGuard = (sliderEl, flickityInstance) => {
  sliderEl[DRAG_GUARD_KEY]?.abort();

  const abort = new AbortController();
  sliderEl[DRAG_GUARD_KEY] = abort;

  let isDragging = false;

  flickityInstance.on("dragStart", () => {
    isDragging = true;
    markFlickityDragStart();
  });

  flickityInstance.on("dragEnd", () => {
    markFlickityDragEnd();
    window.setTimeout(() => {
      isDragging = false;
    }, 100);
  });

  sliderEl.addEventListener(
    "click",
    (event) => {
      const link = event.target.closest?.("a");
      if (!link || !sliderEl.contains(link)) {
        return;
      }

      if (isDragging) {
        event.preventDefault();
        event.stopPropagation();
        isDragging = false;
      }
    },
    { capture: true, signal: abort.signal }
  );
};

const FOCUSABLE_SELECTOR =
  'a, button, input, textarea, select, details, summary, [tabindex]';

/**
 * Flickity sets aria-hidden on off-screen cells; keep focusable children out of tab order.
 */
const syncAriaHiddenFocus = (flickityInstance) => {
  if (!flickityInstance?.element) {
    return;
  }

  let cells = null;
  if (typeof flickityInstance.getCellElements === "function") {
    try {
      cells = flickityInstance.getCellElements();
    } catch {
      cells = null;
    }
  }

  if (!Array.isArray(cells)) {
    cells = [
      ...flickityInstance.element.querySelectorAll(
        ".flickity-cell, .flickity-slider > *"
      ),
    ];
  }

  cells.forEach((cell) => {
    if (!cell?.querySelectorAll) {
      return;
    }
    const isHidden = cell.getAttribute("aria-hidden") === "true";
    cell.querySelectorAll(FOCUSABLE_SELECTOR).forEach((el) => {
      if (isHidden) {
        el.setAttribute("tabindex", "-1");
      } else if (el.getAttribute("tabindex") === "-1") {
        el.removeAttribute("tabindex");
      }
    });
  });
};

const attachAriaHiddenFocusSync = (flickityInstance) => {
  const sync = () => syncAriaHiddenFocus(flickityInstance);
  sync();
  flickityInstance.on("ready", sync);
  flickityInstance.on("select", sync);
  flickityInstance.on("change", sync);
};

const labelFlickityPageDots = (el, flickityInstance) => {
  const template = el.getAttribute("data-slide-aria");
  if (!template) {
    return;
  }

  const apply = () => {
    el.querySelectorAll(".flickity-page-dots .flickity-page-dot").forEach((dot, index) => {
      const label = template.replace("%s", String(index + 1));
      dot.setAttribute("aria-label", label);
      if (dot.childElementCount === 0) {
        dot.textContent = label;
      }
    });
  };

  apply();
  flickityInstance.on("ready", apply);
};

const createFlickity = (el, optionOverrides = {}) => {
  if (!el || el.children.length === 0) {
    return null;
  }

  try {
    const flickityInstance = new Flickity(el, {
      ...parseFlickityOptions(el),
      ...optionOverrides,
      dragThreshold: 10,
    });

    attachDragClickGuard(el, flickityInstance);
    attachAriaHiddenFocusSync(flickityInstance);
    labelFlickityPageDots(el, flickityInstance);
    return flickityInstance;
  } catch (error) {
    console.warn("[flickity] init skipped", el, error);
    return null;
  }
};

export const initDataFlickityElements = (root = document) => {
  root.querySelectorAll("[data-flickity]").forEach((el) => {
    destroyFlickity(el);
    createFlickity(el);
  });
};

export const initBlogCategoryCarousels = (root = document) => {
  root.querySelectorAll(".bloglist--categories").forEach((carousel) => {
    destroyFlickity(carousel);

    const flkty = createFlickity(carousel, {
      pageDots: false,
      watchCSS: true,
      prevNextButtons: false,
      cellAlign: "left",
      accessibility: false,
      contain: true,
    });

    if (!flkty) {
      return;
    }

    const progressBar = carousel
      .closest(".bloglist-wrapper")
      ?.querySelector(".bloglist-progress-bar");

    const syncCategoryProgressVisibility = () => {
      if (!progressBar) {
        return;
      }
      const canScroll =
        flkty.slides.length > 1 && flkty.slideableWidth > flkty.size.innerWidth + 1;
      progressBar.hidden = !canScroll;
      if (!canScroll) {
        progressBar.style.width = "";
      }
    };

    flkty.on("ready", syncCategoryProgressVisibility);
    flkty.on("resize", syncCategoryProgressVisibility);

    flkty.on("scroll", (progress) => {
      if (!progressBar || progressBar.hidden) {
        return;
      }
      progress = Math.max(0, Math.min(1, progress));
      const width = progress * 100;
      progressBar.style.width = `${width < 1 ? 20 : width}%`;
    });
  });
};

export const initBlogSliders = (root = document) => {
  initBlogCategoryCarousels(root);
  initDataFlickityElements(root);
};
